/**
 * Reward Detail Dialog
 * Displays full reward details in a dialog (fullscreen on mobile)
 * Shows QR code prominently for QR-redeemable rewards
 */

import { useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  Divider,
  Avatar,
  Alert,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Tooltip,
  Stack,
} from '@mui/material'
import {
  Close,
  Send,
  CardGiftcard,
  Schedule,
  CalendarMonth,
  Repeat,
  Groups,
  Explore,
  ContentCopy,
  Brightness7,
  BrightnessHigh,
  Warning,
} from '@mui/icons-material'
import { format } from 'date-fns'
import {
  RewardInstanceStatus,
  RewardSourceType,
  GiftRewardContentType,
  getSourceTypeName,
} from '@api/types/rewards'
import type { UserRewardDto, GiftRewardInstanceDto } from '@api/types/rewards'
import { useRewardInstance, useCanTransferReward } from '../hooks/useRewardInstance'
import { RewardStatusChip } from './RewardStatusChip'
import { RewardContentDisplay } from './RewardContentDisplay'
import { TransferModal } from './TransferModal'
import { ThemedQRCode } from '@/components/ThemedQRCode'
import { useAuthStore } from '@store/authStore'
import {
  formatExpirationDate,
  formatRemainingUses,
  getExpirationWarningLevel,
} from '../utils/rewardHelpers'

interface RewardDetailDialogProps {
  open: boolean
  onClose: () => void
  rewardId: string | null
  /** Called after successful transfer */
  onTransferSuccess?: () => void
}

export function RewardDetailDialog({
  open,
  onClose,
  rewardId,
  onTransferSuccess,
}: RewardDetailDialogProps) {
  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const { user } = useAuthStore()

  const [showTransferModal, setShowTransferModal] = useState(false)
  const [brightMode, setBrightMode] = useState(false)
  const [copied, setCopied] = useState(false)

  const { data: reward, isLoading, error } = useRewardInstance(rewardId || undefined)
  const { data: canTransfer } = useCanTransferReward(rewardId || undefined)

  const handleCopyCode = () => {
    if (reward) {
      const code = (reward as any).uniqueCode || ''
      navigator.clipboard.writeText(code)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const handleTransferSuccess = () => {
    setShowTransferModal(false)
    onTransferSuccess?.()
    onClose()
  }

  // Determine if this is a QR-redeemable type that should show QR prominently
  const isQRRedeemable = reward?.template?.contentType === GiftRewardContentType.QR_Redeemable
  const isActive = reward?.status === RewardInstanceStatus.Active
  const isExpired = reward?.status === RewardInstanceStatus.Expired
  const expirationWarning = reward ? getExpirationWarningLevel(reward.expiresAt) : 'none'
  const uniqueCode = reward ? (reward as any).uniqueCode || '' : ''

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        fullScreen={isSmallScreen}
        PaperProps={{
          sx: {
            borderRadius: isSmallScreen ? 0 : 2,
            maxHeight: isSmallScreen ? '100%' : '90vh',
            ...(brightMode && {
              filter: 'brightness(1.3)',
            }),
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            pr: 1,
          }}
        >
          <CardGiftcard color={isActive ? 'primary' : 'action'} />
          <Typography variant="h6" sx={{ flexGrow: 1 }} noWrap>
            {reward?.template?.title || 'Reward Details'}
          </Typography>
          {isActive && (
            <Tooltip title={brightMode ? 'Normal brightness' : 'Increase brightness'}>
              <IconButton onClick={() => setBrightMode(!brightMode)} size="small">
                {brightMode ? <BrightnessHigh /> : <Brightness7 />}
              </IconButton>
            </Tooltip>
          )}
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          )}

          {error && (
            <Alert severity="error">
              Failed to load reward details. Please try again.
            </Alert>
          )}

          {reward && !isLoading && (
            <Stack spacing={2}>
              {/* Status */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <RewardStatusChip status={reward.status} />
                {reward.template?.isTransferable && (
                  <Typography variant="caption" color="text.secondary">
                    Transferable
                  </Typography>
                )}
              </Box>

              {/* Expired warning */}
              {isExpired && (
                <Alert severity="warning" icon={<Warning />}>
                  This reward has expired.
                  {(reward as any).allowPostExpirationRedemption &&
                    ' The business may choose to honor it at their discretion.'}
                </Alert>
              )}

              {/* QR Code Section - prominent for QR_Redeemable or when active */}
              {(isQRRedeemable || isActive) && uniqueCode && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    py: 2,
                    position: 'relative',
                  }}
                >
                  <ThemedQRCode data={uniqueCode} size={isSmallScreen ? 240 : 220} />

                  {isExpired && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%) rotate(-15deg)',
                        pointerEvents: 'none',
                      }}
                    >
                      <Typography
                        sx={{
                          color: 'error.main',
                          fontSize: 36,
                          fontWeight: 'bold',
                          opacity: 0.4,
                          textTransform: 'uppercase',
                        }}
                      >
                        Expired
                      </Typography>
                    </Box>
                  )}

                  {/* Instructions */}
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    textAlign="center"
                    sx={{ mt: 2 }}
                  >
                    Present this QR code to redeem your reward
                  </Typography>

                  {/* Manual code */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mt: 1,
                      p: 1,
                      bgcolor: 'action.hover',
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Code:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontFamily: 'monospace', letterSpacing: 1 }}
                    >
                      {uniqueCode}
                    </Typography>
                    <Tooltip title={copied ? 'Copied!' : 'Copy code'}>
                      <IconButton size="small" onClick={handleCopyCode}>
                        <ContentCopy fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              )}

              {/* Description */}
              {reward.template?.description && (
                <Typography variant="body2" color="text.secondary">
                  {reward.template.description}
                </Typography>
              )}

              <Divider />

              {/* Details Grid */}
              <Box>
                {/* Remaining uses */}
                {reward.remainingUses !== null && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                    <Repeat fontSize="small" color="action" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Remaining Uses
                      </Typography>
                      <Typography variant="body2">
                        {formatRemainingUses(reward.remainingUses)}
                      </Typography>
                    </Box>
                  </Box>
                )}

                {/* Expiration */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                  <Schedule
                    fontSize="small"
                    color={
                      expirationWarning === 'very-soon' || expirationWarning === 'expired'
                        ? 'error'
                        : expirationWarning === 'soon'
                          ? 'warning'
                          : 'action'
                    }
                  />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Expiration
                    </Typography>
                    <Typography
                      variant="body2"
                      color={
                        expirationWarning === 'very-soon' || expirationWarning === 'expired'
                          ? 'error.main'
                          : expirationWarning === 'soon'
                            ? 'warning.main'
                            : 'text.primary'
                      }
                    >
                      {formatExpirationDate(reward.expiresAt)}
                    </Typography>
                  </Box>
                </Box>

                {/* Received date */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                  <CalendarMonth fontSize="small" color="action" />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Received
                    </Typography>
                    <Typography variant="body2">
                      {format(new Date(reward.claimedAt), 'MMM d, yyyy')}
                    </Typography>
                  </Box>
                </Box>

                {/* Owner */}
                {user?.displayName && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                    <Avatar sx={{ width: 20, height: 20, fontSize: 12 }}>
                      {user.displayName[0]}
                    </Avatar>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Owner
                      </Typography>
                      <Typography variant="body2">{user.displayName}</Typography>
                    </Box>
                  </Box>
                )}
              </Box>

              {/* Source section */}
              {reward.source && (
                <>
                  <Divider />
                  <Box>
                    <Typography variant="caption" color="text.secondary" gutterBottom>
                      Source
                    </Typography>

                    {/* Group Source */}
                    {reward.sourceType === RewardSourceType.Group && reward.source?.groupId && (
                      <Box
                        component={RouterLink}
                        to={`/groups/${reward.source.groupId}`}
                        onClick={onClose}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          mt: 1,
                          textDecoration: 'none',
                          color: 'inherit',
                          '&:hover': { opacity: 0.8 },
                        }}
                      >
                        <Avatar
                          src={
                            reward.source.groupThumbnail
                              ? `https://images.hadv.quest/${reward.source.groupThumbnail}`
                              : undefined
                          }
                          variant="rounded"
                          sx={{ width: 36, height: 36 }}
                        >
                          <Groups fontSize="small" />
                        </Avatar>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography variant="body2" fontWeight={600} noWrap>
                            {reward.source.groupName || 'Group'}
                          </Typography>
                        </Box>
                      </Box>
                    )}

                    {/* Adventure Source */}
                    {reward.sourceType === RewardSourceType.Adventure &&
                      reward.source?.adventureId && (
                        <Box
                          component={RouterLink}
                          to={`/adventures/${reward.source.adventureId}`}
                          onClick={onClose}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            mt: 1,
                            textDecoration: 'none',
                            color: 'inherit',
                            '&:hover': { opacity: 0.8 },
                          }}
                        >
                          <Avatar
                            src={
                              reward.source.adventureThumbnail
                                ? `https://images.hadv.quest/${reward.source.adventureThumbnail}`
                                : undefined
                            }
                            variant="rounded"
                            sx={{ width: 36, height: 36 }}
                          >
                            <Explore fontSize="small" />
                          </Avatar>
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography variant="body2" fontWeight={600} noWrap>
                              {reward.source.adventureTitle || 'Adventure'}
                            </Typography>
                          </Box>
                        </Box>
                      )}

                    {/* Other sources */}
                    {(reward.sourceType === RewardSourceType.Manual ||
                      reward.sourceType === RewardSourceType.Transfer ||
                      (!reward.source?.groupId && !reward.source?.adventureId)) && (
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {getSourceTypeName(reward.sourceType)}
                      </Typography>
                    )}
                  </Box>
                </>
              )}

              {/* Transfer count */}
              {reward.transferCount > 0 && (
                <>
                  <Divider />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Transfer History
                    </Typography>
                    <Typography variant="body2">
                      Transferred {reward.transferCount} time
                      {reward.transferCount !== 1 ? 's' : ''}
                    </Typography>
                  </Box>
                </>
              )}

              {/* Can't transfer reason */}
              {reward.template.isTransferable && canTransfer && !canTransfer.canTransfer && (
                <Alert severity="info" variant="outlined">
                  {canTransfer.reason || 'This reward cannot be transferred at this time.'}
                </Alert>
              )}
            </Stack>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 2, py: 1.5 }}>
          {canTransfer?.canTransfer && reward?.template.isTransferable && (
            <Button
              variant="outlined"
              startIcon={<Send />}
              onClick={() => setShowTransferModal(true)}
            >
              Transfer
            </Button>
          )}
          <Box sx={{ flex: 1 }} />
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Transfer Modal */}
      {reward && (
        <TransferModal
          open={showTransferModal}
          onClose={() => setShowTransferModal(false)}
          rewardId={reward.id}
          rewardTitle={reward.template?.title || 'Untitled Reward'}
          onSuccess={handleTransferSuccess}
        />
      )}
    </>
  )
}
